import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { UnityGame, OnboardingFlow, ProfilePage, RoomsDashboard, Sidebar, PreferencesPanel, View, Login, GameChat, SocialPlayPanel, MetaTags, DiscoveryPage, ChatPage, ExplorePage } from './components';
import { PlayerProfileOverlay } from './components/PlayerProfileOverlay';
import { PlayerSheet, SheetPlayer } from './components/game/PlayerSheet';
import { RoomPickerSheet } from './components/game/RoomPickerSheet';
import { TimeTravelOverlay } from './components/game/TimeTravelOverlay';
import { GamePanel } from './components/game/GamePanel';
import { useRoomInvites } from './hooks/useRoomInvites';
import { roomInviteService, MyRoomSummary } from './services/api/roomInviteService';
import { WorldMessageToast } from './components/WorldMessageToast';
import { CallPanel } from './components/CallPanel';
import { profileService } from './services/api';
import { SocialPanel } from './components/social';
import { RoomJoin } from './components/RoomJoin';
import { ShareRoomButton } from './components/ShareRoomButton';
import { BillingPage, BillingHistoryPage } from './components/billing';
import { UserProvider, useUser } from './context/UserContext';
import { RoomProvider, useRooms } from './context/RoomContext';
import { SignalRProvider } from './context/SignalRContext';
import { CharacterProvider, useCharacter } from './context/CharacterContext';
import { BillingProvider } from './context/BillingContext';
import { ThemeProvider, getThemeFromUrl } from './context/ThemeContext';
// Profiles & Social Connections — all inert until their `social.*` flags flip.
import { ProfileCardProvider } from './context/ProfileCardContext';
import { SocialGraphProvider } from './context/SocialGraphContext';
import { PresenceProvider } from './context/PresenceContext';
import { ActivityProvider } from './context/ActivityContext';
import { signalRService } from './services/signalr/SignalRService';
import { HubEvents } from './services/signalr/types';
import { useOrientationLock, isMobileDevice } from './hooks';
import { appConfig } from './config';
import { Room, RoomPlayer } from './types/user';
import { GameInitParams, UnityGameState, UnityGameEvent, UnityGameError, UnityPlayer, OtherPlayerInfo } from './types';
import { GuidedNpc, resolveGuide, GUIDE_ROOM_CODE, DEFAULT_GUIDE_SLUG } from './data/guidedNpcs';
import { sendGuideToNpc } from './utils/gameEmote';
import { roomThumbUrl } from './utils/inviteLink';
import './App.css';
import './styles/themes.css'; // Must be last to override with theme colors

// SignalR server base URL for Unity - Unity's SignalRConfig appends the hub name
const SIGNALR_SERVER_URL = import.meta.env.VITE_SIGNALR_URL || 'https://sync.direct';

type AppView = 'login' | 'register' | 'discover' | 'messages' | 'rooms' | 'explore' | 'game' | 'connections' | 'preferences';

interface PlayerNotification {
  id: string;
  type: 'join' | 'leave';
  playerName: string;
}

// Parse URL parameters for deep link handling
function getJoinParams(): { code: string | null; from: string | null; ask: string | null } {
  if (typeof window === 'undefined') return { code: null, from: null, ask: null };
  const urlParams = new URLSearchParams(window.location.search);
  return {
    code: urlParams.get('join'),
    from: urlParams.get('from'),
    ask: urlParams.get('ask'), // WU-W5 second door: ?ask=<guide slug>
  };
}

function AppContent() {
  const { user, isLoading, isAuthReady, isConnected: userConnected, connectionError, authError, loginWithCredentials } = useUser();
  const { setCurrentRoom, currentRoom, leaveRoom, isConnected: roomsConnected, rooms, joinRoom, refreshRooms } = useRooms();
  const { activeCharacter } = useCharacter();
  // If currentRoom is set (e.g. navigated from /room/:code), start in game view;
  // otherwise land dating-first on Discover.
  const [currentView, setCurrentView] = useState<AppView>(() => currentRoom ? 'game' : 'discover');
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  // Dating-profile completion nudge: null = no profile yet, number = percent.
  const [profileCompletion, setProfileCompletion] = useState<number | null>(null);
  const [completionChecked, setCompletionChecked] = useState(false);
  const [showResumeWizard, setShowResumeWizard] = useState(false);
  const [setupBannerDismissed, setSetupBannerDismissed] = useState(false);
  // Peek-visibility one-time nudge (V2 Amethyst WU-02, R6): honest, opt-in,
  // shown once ever after a user's first room join, never auto-flips anything.
  const [showPeekPrompt, setShowPeekPrompt] = useState(false);
  const [_isUnityReady, setIsUnityReady] = useState(false);
  const [gameState, setGameState] = useState<UnityGameState>('waiting_for_params');
  const [gamePlayers, setGamePlayers] = useState<UnityPlayer[]>([]);
  const [notifications, setNotifications] = useState<PlayerNotification[]>([]);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  // In-game social surfaces: tapped-player profile + Unity vicinity set.
  // nearbyIds === null → no vicinity data from Unity (fail open in chat).
  const [viewProfile, setViewProfile] = useState<{ userId: string; displayName?: string } | null>(null);
  const [nearbyIds, setNearbyIds] = useState<string[] | null>(null);
  // P1 mobile-declutter HUD state: the tapped-player interaction sheet, the
  // floating-bubble chat visibility (bare by default on mobile, docked on
  // desktop), unread count while the bubble is closed, the expandable header
  // roster, and the sheet→chat "talk to this player" hand-off target.
  const [sheetPlayer, setSheetPlayer] = useState<SheetPlayer | null>(null);
  const [isChatOpen, setIsChatOpen] = useState<boolean>(() => !isMobileDevice());
  const [chatUnread, setChatUnread] = useState(0);
  const [rosterExpanded, setRosterExpanded] = useState(false);
  const [chatRequestTarget, setChatRequestTarget] = useState<{ id: string; name: string } | null>(null);
  // P3 room invites + time travel: the player being invited (drives the room
  // picker), and whether the current room is saved (★ header toggle).
  const [invitePickerTarget, setInvitePickerTarget] = useState<SheetPlayer | null>(null);
  const [currentRoomSaved, setCurrentRoomSaved] = useState<boolean | null>(null);
  const { incomingInvite, travel, note: inviteNote, sendInviteToRoom, respondInvite } = useRoomInvites({
    currentRoom,
    user,
    gameState,
  });

  // Deep link state
  const [pendingJoinCode, setPendingJoinCode] = useState<string | null>(() => {
    const params = getJoinParams();
    if (params.code) return params.code;
    // WU-W5: a shareable ?ask=<slug> deep-link arms the guide's room join on
    // mount so the existing auto-join effect carries it into the world (AC-3).
    const guide = resolveGuide(params.ask);
    return guide ? (guide.roomCode ?? GUIDE_ROOM_CODE) : null;
  });
  const [inviterName, setInviterName] = useState<string | null>(() => getJoinParams().from);
  const [joinError, setJoinError] = useState<string | null>(null);
  // WU-W5 second door: the guide NPC we're navigating to (from ?ask= or a CTA).
  const [pendingGuideNpc, setPendingGuideNpc] = useState<GuidedNpc | null>(
    () => resolveGuide(getJoinParams().ask) ?? null
  );
  const [guideError, setGuideError] = useState<string | null>(null);
  const joinAttemptedRef = useRef(false);

  const isConnected = userConnected || roomsConnected;

  // Orientation lock: portrait on mobile except in game (which allows both)
  const { lock, unlock } = useOrientationLock();

  useEffect(() => {
    if (!isMobileDevice()) return;

    if (currentView === 'game') {
      // Game view: allow both portrait and landscape
      unlock();
    } else {
      // Other views: lock to portrait for better mobile UX
      lock('portrait');
    }
  }, [currentView, lock, unlock]);

  // Show player join/leave notifications
  const showNotification = useCallback((type: 'join' | 'leave', playerName: string) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, type, playerName }]);

    // Remove notification after 3 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  }, []);

  // Subscribe to player events for notifications
  useEffect(() => {
    const unsubJoin = signalRService.on<[string, string]>(
      HubEvents.PlayerJoinedRoom,
      (roomId, playerJson) => {
        if (currentRoom && currentRoom.id === roomId) {
          try {
            const player = JSON.parse(playerJson);
            if (player.id !== user?.id) {
              showNotification('join', player.displayName || player.playerName);
            }
          } catch (e) {
            console.error('Failed to parse player data:', e);
          }
        }
      }
    );

    const unsubLeave = signalRService.on<[string, string]>(
      HubEvents.PlayerLeftRoom,
      (roomId, playerId) => {
        if (currentRoom && currentRoom.id === roomId) {
          const player = currentRoom.players.find(p => p.id === playerId);
          if (player && player.id !== user?.id) {
            showNotification('leave', player.displayName);
          }
        }
      }
    );

    return () => {
      unsubJoin();
      unsubLeave();
    };
  }, [currentRoom, user, showNotification]);

  const handleGameLoaded = useCallback(() => {
    console.log('Unity game loaded successfully');
  }, []);

  const handleUnityReady = useCallback(() => {
    console.log('Unity is ready to receive game parameters');
    setIsUnityReady(true);
  }, []);

  const handleGameStateChange = useCallback((state: UnityGameState) => {
    console.log('Game state changed:', state);
    setGameState(state);

    // Handle specific state changes
    if (state === 'disconnected' || state === 'error') {
      // Could show reconnect button or auto-return to rooms
    }
  }, []);

  const handleGameEvent = useCallback((event: UnityGameEvent) => {
    console.log('Game event:', event.eventName, event.data);

    switch (event.eventName) {
      case 'game_started':
        console.log('Game started successfully');
        break;
      case 'game_left':
        // Player left the game
        setCurrentRoom(null);
        setCurrentView('rooms');
        break;
      case 'player_joined':
        console.log('Player joined:', event.data);
        break;
      case 'player_left':
        console.log('Player left:', event.data);
        break;
      default:
        // Handle other events
        break;
    }
  }, [setCurrentRoom]);

  const handleGameError = useCallback((error: UnityGameError) => {
    console.error('Game error:', error.code, error.message);
    // Could show error toast or modal here
  }, []);

  const handlePlayersUpdate = useCallback((players: UnityPlayer[]) => {
    console.log('Players update:', players);
    setGamePlayers(players);
  }, []);

  // Someone to talk to? A remote human in the Unity roster, a second player in the
  // room roster, or anyone in earshot. Gates the chat chrome — an empty room shows
  // no chat panel/bubble at all.
  const hasCompany = useMemo(() => {
    if (gamePlayers.some((p) => !p.isLocal)) return true;
    if ((currentRoom?.players?.length ?? 0) > 1) return true;
    if ((nearbyIds?.length ?? 0) > 0) return true;
    return false;
  }, [gamePlayers, currentRoom, nearbyIds]);

  // RTS battle rooms always show the chat chrome (Unity 'rtsMode' bridge event):
  // commanders coordinate 2v1 / taunt in 1v1 over room chat, and allies may join
  // mid-battle — the panel must already be there.
  const [rtsActive, setRtsActive] = useState(false);
  useEffect(() => {
    const onUnityEvent = (e: Event) => {
      const detail = (e as CustomEvent<{ eventName?: string; data?: unknown }>).detail;
      if (detail?.eventName !== 'rtsMode') return;
      try {
        const d = detail.data;
        const p = typeof d === 'string' ? JSON.parse(d || '{}') : ((d as Record<string, unknown>) ?? {});
        setRtsActive(!!(p as { active?: boolean }).active);
      } catch { /* keep last known state */ }
    };
    window.addEventListener('unity-event', onUnityEvent);
    return () => window.removeEventListener('unity-event', onUnityEvent);
  }, []);
  const showChat = hasCompany || rtsActive;

  // Resolve a player id to a sheet entry from whichever roster has it (the
  // Unity roster and the SignalR roster differ — current-state-map §1.3/§3.1).
  const resolveSheetPlayer = useCallback((id: string, fallbackName?: string): SheetPlayer => {
    const rp = currentRoom?.players.find((p) => p.id === id);
    const up = gamePlayers.find((p) => p.playerId === id);
    return {
      id,
      name: rp?.displayName || up?.playerName || fallbackName || 'Player',
      isHost: rp?.isHost,
    };
  }, [currentRoom, gamePlayers]);

  // P1: a tapped player (3D avatar OR header pill). NPC ids keep the existing
  // PlayerProfileOverlay (character-guided-profiling chat); humans open the
  // new interaction sheet.
  const handleOpenProfile = useCallback((playerId: string, displayName?: string) => {
    if (playerId.startsWith('npc_')) {
      setViewProfile({ userId: playerId, displayName });
      return;
    }
    if (user && playerId === user.id) return; // don't open a sheet on yourself
    setSheetPlayer(resolveSheetPlayer(playerId, displayName));
  }, [resolveSheetPlayer, user]);

  // Opening the chat bubble clears the unread badge.
  const openChat = useCallback(() => {
    setIsChatOpen(true);
    setChatUnread(0);
  }, []);

  // P3: ★ save/unsave the current room (optimistic; reverts on failure).
  const toggleSaveCurrentRoom = useCallback(async () => {
    if (!currentRoom) return;
    const next = !(currentRoomSaved ?? false);
    setCurrentRoomSaved(next);
    const ok = next
      ? await roomInviteService.saveRoom(currentRoom.id)
      : await roomInviteService.unsaveRoom(currentRoom.id);
    if (!ok) setCurrentRoomSaved(!next);
  }, [currentRoom, currentRoomSaved]);

  const handleNearbyPlayers = useCallback((ids: string[]) => {
    setNearbyIds(ids);
  }, []);

  // Reset the ★ saved-state indicator when the room changes (P3).
  useEffect(() => {
    setCurrentRoomSaved(null);
  }, [currentRoom?.id]);

  const navigate = useNavigate();

  const handleJoinRoom = useCallback((room: Room) => {
    // Room joining is already handled by RoomsDashboard calling joinRoom
    // Set the room, then navigate to /room/:code so the URL reflects the room
    setCurrentRoom(room);
    setCurrentView('game');
    if (room.code) {
      navigate(`/room/${room.code}`, { replace: true });
    }
  }, [setCurrentRoom, navigate]);

  // WU-W5 second door: "go ask <guide> about you". Arms a join to the guide's
  // room and lets the guided-open effect open that NPC's chat once in-game.
  // Coexists with the wizard — it never writes profile data (navigation only).
  const startGuidedNpc = useCallback((slug: string) => {
    const guide = resolveGuide(slug) ?? resolveGuide(DEFAULT_GUIDE_SLUG)!;
    // Exit any onboarding takeover so OnboardingFlow unmounts and the world shows.
    setShowResumeWizard(false);
    setCurrentView((view) => (view === 'register' ? 'rooms' : view));
    // Shareable URL (best-effort; harmless if it throws in a sandboxed frame).
    try {
      window.history.replaceState({}, '', `?ask=${guide.slug}`);
    } catch {
      /* no-op: URL update is a convenience, not required for the flow */
    }
    setGuideError(null);
    setPendingGuideNpc(guide);
    const targetCode = guide.roomCode ?? GUIDE_ROOM_CODE;
    if (currentRoom?.code === targetCode) {
      // Already in the guide's room — the guided-open effect handles it once in_game.
      return;
    }
    joinAttemptedRef.current = false;
    setPendingJoinCode(targetCode);
  }, [currentRoom]);

  const handleLeaveGame = useCallback(async () => {
    // Tell Unity to leave the game
    const windowWithUnity = window as Window & { leaveUnityGame?: () => void };
    windowWithUnity.leaveUnityGame?.();

    // Leave room via SignalR
    if (currentRoom && user) {
      await leaveRoom(currentRoom.id, user.id);
    }

    // Reset local state
    setCurrentRoom(null);
    setCurrentView('rooms');
    setGameState('waiting_for_params');
    setGamePlayers([]);
    setViewProfile(null);
    setNearbyIds(null);
    setSheetPlayer(null);
    setChatRequestTarget(null);
    setRosterExpanded(false);
    setChatUnread(0);
    setInvitePickerTarget(null);
    setCurrentRoomSaved(null);

    // Navigate back to home
    navigate('/', { replace: true });
  }, [currentRoom, user, leaveRoom, setCurrentRoom, navigate]);

  const handleViewChange = (view: View) => {
    if (view === 'profile') {
      setShowProfileSetup(true);
    } else if (view === 'billing') {
      navigate('/billing');
    } else {
      setCurrentView(view as AppView);
    }
  };

  const handleLogin = useCallback(async (email: string, password: string, rememberMe = false) => {
    return await loginWithCredentials(email, password, rememberMe);
  }, [loginWithCredentials]);

  const handleShowRegister = useCallback(() => {
    setCurrentView('register');
  }, []);

  const handleRegistrationComplete = useCallback(() => {
    setShowProfileSetup(false);
    setCurrentView('discover');
    setCompletionChecked(false); // re-fetch completion for the nudge banner
  }, []);

  // Fetch dating-profile completion once auth is ready — drives the
  // "finish setting up your profile" nudge for users who quit mid-wizard.
  useEffect(() => {
    if (!user || !isAuthReady || completionChecked) return;
    let cancelled = false;
    profileService.getCompletionStatus().then((status) => {
      if (cancelled) return;
      setProfileCompletion(status ? status.completionPercentage : null);
      setCompletionChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [user, isAuthReady, completionChecked]);

  // Peek-visibility one-time nudge (V2 Amethyst WU-02, R6): after the user's
  // first room join, if they're still on the default initials-only setting,
  // offer to turn it on — honestly framed, never silently flipped. Shown at
  // most once ever per user (localStorage flag), regardless of how they
  // respond, so it never nags on subsequent joins.
  useEffect(() => {
    if (!user || !isAuthReady || !currentRoom) return;
    const seenKey = `gamedate_peek_prompt_seen_${user.id}`;
    if (localStorage.getItem(seenKey)) return;
    let cancelled = false;
    profileService.getMyProfile().then((profile) => {
      if (cancelled || !profile) return;
      localStorage.setItem(seenKey, '1');
      if (!profile.peekVisibility || profile.peekVisibility === 'initials-only') {
        setShowPeekPrompt(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user, isAuthReady, currentRoom]);

  const handlePeekPromptChoice = useCallback(async (visibility: 'everyone' | 'matches-only') => {
    setShowPeekPrompt(false);
    try {
      await profileService.updateProfile({ peekVisibility: visibility });
    } catch {
      // Non-fatal — the user can still change this later via Profile → Dating → About.
    }
  }, []);

  // Dating profile id — ChatPage needs it (with the JWT) for live message sockets.
  const [datingProfileId, setDatingProfileId] = useState<string | null>(null);
  useEffect(() => {
    if (!user || !isAuthReady) {
      setDatingProfileId(null);
      return;
    }
    let cancelled = false;
    profileService.getMyProfile().then((p) => {
      if (!cancelled) setDatingProfileId(p?.id ?? null);
    }).catch(() => { /* no dating profile yet — ChatPage falls back to REST */ });
    return () => {
      cancelled = true;
    };
  }, [user, isAuthReady]);

  // Build game params when we have a room and user
  const gameParams: GameInitParams | undefined = useMemo(() => {
    if (!currentRoom || !user || currentView !== 'game') {
      return undefined;
    }

    // Use active character's data if available, otherwise fall back to user profile
    const charGender = activeCharacter?.gender || user.gender;
    const charClass = activeCharacter?.characterClass || user.characterClass;
    
    // Convert gender to legacy format for Unity (male/female)
    let legacyGender: 'male' | 'female' = 'male';
    if (charGender === 'woman' || charGender === 'female') {
      legacyGender = 'female';
    }
    const genderSuffix = legacyGender === 'female' ? 'Girl' : 'Boy';
    const characterType = `${charClass}_${genderSuffix}`;

    // Get appearance data from active character (Synty customization JSON)
    const appearanceData = activeCharacter?.appearanceData;

    // Build list of other players already in the room
    const existingPlayers: OtherPlayerInfo[] = currentRoom.players
      .filter((player: RoomPlayer) => player.id !== user.id)
      .map((player: RoomPlayer, index: number) => ({
        playerId: player.id,
        displayName: player.displayName,
        avatar: player.avatar,
        characterType: player.characterType,
        gender: player.gender as 'male' | 'female' | undefined,
        appearanceData: player.appearanceData, // Include other player's appearance
        slotIndex: index + 1, // Slot 0 is for local player
        isHost: player.isHost,
      }));

    return {
      roomId: currentRoom.id,
      playerId: user.id,
      playerName: user.displayName,
      characterType,
      gender: legacyGender, // User's gender for PlayerIdentity (male/female)
      avatar: user.avatar,
      appearanceData, // Synty character customization data
      serverUrl: SIGNALR_SERVER_URL,
      // accessToken: authToken, // Add if using authentication
      characterConfig: {
        displayName: user.displayName,
      },
      roomConfig: {
        maxPlayers: currentRoom.maxPlayers,
        gameMode: currentRoom.context,
        useDynamicScene: true,
        // sceneName: getSceneForContext(currentRoom.context),
      },
      existingPlayers,
    };
  }, [currentRoom, user, currentView, activeCharacter]);

  // Listen for Unity events (legacy compatibility)
  useEffect(() => {
    const handleUnityEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ eventName: string; data: string }>;
      console.log('Received Unity event:', customEvent.detail);
    };

    window.addEventListener('unity-event', handleUnityEvent);
    return () => {
      window.removeEventListener('unity-event', handleUnityEvent);
    };
  }, []);

  // Update document title
  useEffect(() => {
    document.title = appConfig.name;
  }, []);

  // Handle deep link auto-join: when URL has ?join=CODE
  useEffect(() => {
    // Skip if no pending join code, or already attempted, or prerequisites not met
    if (!pendingJoinCode || joinAttemptedRef.current || !user || !isConnected || rooms.length === 0) {
      return;
    }

    const attemptJoin = async () => {
      joinAttemptedRef.current = true;

      // Find room by code
      const targetRoom = rooms.find(r => r.code === pendingJoinCode);

      if (!targetRoom) {
        console.warn(`[DeepLink] Room with code "${pendingJoinCode}" not found`);
        setJoinError(`Room "${pendingJoinCode}" not found. It may have been deleted or the code is invalid.`);
        setPendingJoinCode(null);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      console.log(`[DeepLink] Found room "${targetRoom.name}" (${targetRoom.id}) for code "${pendingJoinCode}"`);

      try {
        // Build character type from user profile
        const genderSuffix = user.gender === 'female' ? 'Girl' : 'Boy';
        const characterType = `${user.characterClass}_${genderSuffix}`;

        const success = await joinRoom(
          targetRoom.id,
          user.id,
          user.displayName,
          user.avatar,
          characterType,
          user.gender
        );

        if (success) {
          console.log(`[DeepLink] Successfully joined room "${targetRoom.name}"`);
          setCurrentRoom(targetRoom);
          setCurrentView('game');
          setPendingJoinCode(null);
          setInviterName(null);
          // Navigate to room URL
          if (targetRoom.code) {
            navigate(`/room/${targetRoom.code}`, { replace: true });
          }
        }
      } catch (error) {
        const err = error as Error;
        console.error('[DeepLink] Failed to join room:', err);

        if (err.message === 'PASSWORD_REQUIRED') {
          setJoinError('This room requires a password.');
        } else if (err.message === 'APPROVAL_REQUIRED') {
          setJoinError('This room requires host approval to join.');
        } else {
          setJoinError('Failed to join room. Please try again.');
        }
        setPendingJoinCode(null);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
      }
    };

    attemptJoin();
  }, [pendingJoinCode, user, isConnected, rooms, joinRoom, setCurrentRoom]);

  // Refresh rooms when we have a pending join code and connection is ready
  useEffect(() => {
    if (pendingJoinCode && isConnected && rooms.length === 0 && !joinAttemptedRef.current) {
      console.log('[DeepLink] Refreshing rooms to find join code:', pendingJoinCode);
      refreshRooms();
    }
  }, [pendingJoinCode, isConnected, rooms.length, refreshRooms]);

  // WU-W5 guided-open: once in the guide's room and the NPC roster has arrived,
  // open that NPC's chat (PlayerProfileOverlay NPC mode) and nudge the local
  // avatar toward them. Keyed on gamePlayers so it re-runs when the roster
  // arrives after in_game (roster can lag the state transition).
  useEffect(() => {
    if (!pendingGuideNpc || gameState !== 'in_game') return;
    const target = gamePlayers.find(
      (p) =>
        p.playerId.startsWith('npc_') &&
        p.playerName?.toLowerCase() === pendingGuideNpc.displayName.toLowerCase()
    );
    if (!target) return; // roster may arrive later; effect re-runs on gamePlayers change
    setViewProfile({ userId: target.playerId, displayName: target.playerName });
    sendGuideToNpc(target.playerId); // spawn-proximity: Unity may walk/teleport (no-ops safely)
    setPendingGuideNpc(null);
  }, [pendingGuideNpc, gameState, gamePlayers]);

  // WU-W5 fallback: if the guide never appears within ~6s of reaching in_game,
  // degrade to a soft, dismissible error instead of hanging on a missing NPC.
  useEffect(() => {
    if (!pendingGuideNpc || gameState !== 'in_game') return;
    const guideName = pendingGuideNpc.displayName;
    const timer = window.setTimeout(() => {
      setGuideError(`Couldn't find ${guideName} here right now — try tapping them in the room.`);
      setPendingGuideNpc(null);
    }, 6000);
    return () => window.clearTimeout(timer);
  }, [pendingGuideNpc, gameState]);

  // WU-W5: a failed room join (deep-link auto-join error) clears any dangling
  // guide so we never wait for an NPC in a room we never entered.
  useEffect(() => {
    if (joinError) setPendingGuideNpc(null);
  }, [joinError]);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show login if not logged in
  if (!user && currentView !== 'register') {
    return (
      <Login
        onLogin={handleLogin}
        onRegister={handleShowRegister}
        error={authError}
      />
    );
  }

  // New registration: account → 7-stage profile wizard → character look
  if (currentView === 'register') {
    return (
      <OnboardingFlow
        onComplete={handleRegistrationComplete}
        onBackToLogin={() => setCurrentView('rooms')}
        onAskNpc={startGuidedNpc}
      />
    );
  }

  // Logged-in user resuming an incomplete profile from the nudge banner
  if (showResumeWizard && user) {
    return (
      <OnboardingFlow
        startAtWizard
        onComplete={() => {
          setShowResumeWizard(false);
          setCompletionChecked(false);
        }}
        onBackToLogin={() => setShowResumeWizard(false)}
        onAskNpc={startGuidedNpc}
      />
    );
  }

  // Show profile page for editing existing profile (tabbed interface)
  if (showProfileSetup && user) {
    return (
      <ProfilePage onClose={() => setShowProfileSetup(false)} />
    );
  }

  return (
    <div className={`app-layout ${currentView === 'game' ? 'app-layout--in-game' : ''}`}>
      <MetaTags
        title={currentRoom ? `${currentRoom.name} — ${appConfig.name}` : undefined}
        description={currentRoom
          ? `${currentRoom.hostName} is hosting "${currentRoom.name}". ${currentRoom.currentPlayers}/${currentRoom.maxPlayers} players — join the adventure!`
          : undefined
        }
        image={currentRoom?.sceneTemplateId ? roomThumbUrl(currentRoom) : undefined}
        url={currentRoom ? `https://gamedate.co.il/room/${currentRoom.code}` : undefined}
      />
      <Sidebar currentView={currentView as View} onViewChange={handleViewChange} />

      <main className="main-content">
        {/* Connection status indicator */}
        {!isConnected && (
          <div className="connection-status disconnected">
            <span className="status-dot"></span>
            {connectionError || 'Connecting to server...'}
          </div>
        )}

        {/* Player notifications */}
        {notifications.map((notification) => (
          <div key={notification.id} className={`player-notification ${notification.type}`}>
            <span className="player-notification-text">
              {notification.type === 'join'
                ? `${notification.playerName} joined the room`
                : `${notification.playerName} left the room`
              }
            </span>
          </div>
        ))}

        {/* Deep link join error */}
        {joinError && (
          <div className="join-error-banner">
            <span>{joinError}</span>
            <button onClick={() => setJoinError(null)} className="dismiss-btn">×</button>
          </div>
        )}

        {/* WU-W5 second-door (guided NPC) error — soft, dismissible */}
        {guideError && (
          <div className="join-error-banner">
            <span>{guideError}</span>
            <button onClick={() => setGuideError(null)} className="dismiss-btn">×</button>
          </div>
        )}

        {/* Invite welcome message */}
        {pendingJoinCode && inviterName && currentView === 'rooms' && (
          <div className="invite-banner">
            <span>{decodeURIComponent(inviterName)} invited you to join a room. Connecting...</span>
          </div>
        )}

        {/* Profile-completion nudge: profile missing or under 60% complete */}
        {(currentView === 'rooms' || currentView === 'discover') && completionChecked && !setupBannerDismissed &&
          (profileCompletion === null || profileCompletion < 60) && (
          <div className="profile-nudge-banner">
            <span>
              {profileCompletion === null
                ? 'Set up your dating profile so people can get to know you.'
                : `Your profile is ${profileCompletion}% complete — the more you share, the better your matches.`}
            </span>
            <button className="profile-nudge-cta" onClick={() => setShowResumeWizard(true)}>
              Continue setup
            </button>
            {/* WU-W5 second door: skip the forms and go meet Mara in-world. */}
            <button className="profile-nudge-secondary" onClick={() => startGuidedNpc(DEFAULT_GUIDE_SLUG)}>
              Skip the forms — go ask Mara about you
            </button>
            <button
              className="profile-nudge-dismiss"
              onClick={() => setSetupBannerDismissed(true)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Peek-visibility one-time nudge (V2 Amethyst WU-02, R6) — honest
            framing, links directly to the setting, never auto-flips. */}
        {currentView === 'game' && showPeekPrompt && (
          <div className="peek-visibility-banner">
            <span>
              Right now your room cards show initials only. Want people to see your photo and
              first name before they say hi?
            </span>
            <button className="peek-visibility-cta" onClick={() => handlePeekPromptChoice('everyone')}>
              Show to everyone
            </button>
            <button className="peek-visibility-secondary" onClick={() => handlePeekPromptChoice('matches-only')}>
              Only for matches
            </button>
            <button
              className="peek-visibility-dismiss"
              onClick={() => setShowPeekPrompt(false)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {currentView === 'discover' && (
          <DiscoveryPage onOpenMessages={() => setCurrentView('messages')} />
        )}

        {currentView === 'messages' && (
          <ChatPage
            accessToken={localStorage.getItem('gamedate_token') ?? undefined}
            profileId={datingProfileId ?? undefined}
          />
        )}

        {currentView === 'rooms' && (
          <RoomsDashboard onJoinRoom={handleJoinRoom} />
        )}

        {currentView === 'explore' && (
          <ExplorePage onJoinRoom={handleJoinRoom} />
        )}

        {currentView === 'game' && (
          <div className="game-view">
            {currentRoom && (
              <div className="room-header-bar">
                <button
                  className="back-btn"
                  onClick={handleLeaveGame}
                >
                  Leave Game
                </button>
                <div className="current-room-info">
                  <h2>{currentRoom.name}</h2>
                  <span>
                    {(() => {
                      // NPCs live in the roster but never occupy a player slot.
                      const humans = gamePlayers.filter((p) => !p.playerId.startsWith('npc_')).length;
                      const npcs = gamePlayers.length - humans;
                      const base = humans > 0
                        ? `${humans}/${currentRoom.maxPlayers} players`
                        : `${currentRoom.currentPlayers}/${currentRoom.maxPlayers} players`;
                      return npcs > 0 ? `${base} · ${npcs} locals` : base;
                    })()}
                  </span>
                  {gameState !== 'in_game' && gameState !== 'waiting_for_params' && (
                    <span className="game-status">({getStatusText(gameState)})</span>
                  )}
                  {/* P3: save this room (pins it to the top of the invite picker). */}
                  <button
                    className={`room-save-btn ${currentRoomSaved ? 'saved' : ''}`}
                    onClick={toggleSaveCurrentRoom}
                    title={currentRoomSaved ? 'Saved — tap to remove' : 'Save this room'}
                    aria-pressed={!!currentRoomSaved}
                  >
                    {currentRoomSaved ? '★' : '☆'}
                  </button>
                  {/* Invite friends to THIS room (WhatsApp/Email/Copy + note + who-invited) */}
                  <ShareRoomButton room={currentRoom} variant="button" label="Invite" className="room-header-invite" />
                </div>
                <div className="room-players-list">
                  <button
                    className="room-roster-chip"
                    onClick={() => setRosterExpanded((v) => !v)}
                    aria-expanded={rosterExpanded}
                    title="Players in this room — tap someone to chat, gesture or call"
                  >
                    👥 {currentRoom.players.length}
                  </button>
                  <div className={`room-players-pills ${rosterExpanded ? 'expanded' : ''}`}>
                    {currentRoom.players.map((player) => {
                      const isSelf = player.id === user?.id;
                      const near = nearbyIds?.includes(player.id) ?? false;
                      return (
                        <button
                          key={player.id}
                          className={`player-tag ${player.isHost ? 'host' : ''}`}
                          disabled={isSelf}
                          onClick={() => {
                            if (isSelf) return;
                            handleOpenProfile(player.id, player.displayName);
                            setRosterExpanded(false);
                          }}
                          title={isSelf ? 'You' : `Open ${player.displayName}`}
                        >
                          {!isSelf && (
                            <span className={`player-tag-dot ${near ? 'near' : 'far'}`} aria-hidden="true" />
                          )}
                          {player.displayName}
                          {player.isHost && ' (Host)'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <div className="game-container">
              <UnityGame
                config={appConfig}
                onLoaded={handleGameLoaded}
                gameParams={gameParams}
                onUnityReady={handleUnityReady}
                onGameStateChange={handleGameStateChange}
                onGameEvent={handleGameEvent}
                onGameError={handleGameError}
                onPlayersUpdate={handlePlayersUpdate}
                onOpenProfile={handleOpenProfile}
                onNearbyPlayers={handleNearbyPlayers}
              />
            </div>

            {/* Tapped-avatar profile card (in-game) — NPCs + "full profile" */}
            {viewProfile && (
              <PlayerProfileOverlay
                userId={viewProfile.userId}
                displayName={viewProfile.displayName}
                isNearby={nearbyIds?.includes(viewProfile.userId) ?? true}
                roomId={currentRoom?.id}
                onClose={() => setViewProfile(null)}
              />
            )}

            {/* P1: tapped-player interaction sheet (humans only) */}
            {sheetPlayer && (
              <PlayerSheet
                player={sheetPlayer}
                isNearby={nearbyIds?.includes(sheetPlayer.id) ?? true}
                invitesEnabled
                onInvite={() => {
                  setInvitePickerTarget(sheetPlayer);
                  setSheetPlayer(null);
                }}
                onChat={() => {
                  setChatRequestTarget({ id: sheetPlayer.id, name: sheetPlayer.name });
                  openChat();
                  setSheetPlayer(null);
                }}
                onGesture={() => {
                  window.dispatchEvent(new CustomEvent('gamedate-open-emotebar'));
                  setSheetPlayer(null);
                }}
                onCall={() => {
                  window.dispatchEvent(
                    new CustomEvent('gamedate-call-request', {
                      detail: { userId: sheetPlayer.id, displayName: sheetPlayer.name },
                    })
                  );
                  setSheetPlayer(null);
                }}
                onFullProfile={() => {
                  setViewProfile({ userId: sheetPlayer.id, displayName: sheetPlayer.name });
                  setSheetPlayer(null);
                }}
                onClose={() => setSheetPlayer(null)}
              />
            )}

            {/* Approval-gated "phone call" to players out of earshot */}
            {currentRoom && <CallPanel roomId={currentRoom.id} />}

            {/* In-game chat — when there is someone to talk to (a remote player in the room
                or in earshot), or ALWAYS in an RTS battle room (commanders coordinate here);
                an empty social room shows no chat chrome at all. */}
            {currentRoom && showChat && (
              <GameChat
                roomId={currentRoom.id}
                isMinimized={isChatMinimized}
                onToggleMinimize={() => setIsChatMinimized(!isChatMinimized)}
                nearbyPlayerIds={nearbyIds}
                roomPlayers={gamePlayers}
                open={isChatOpen}
                mobileSheet={isMobileDevice()}
                onClose={() => setIsChatOpen(false)}
                requestPrivateTalkWith={chatRequestTarget}
                onPrivateTalkConsumed={() => setChatRequestTarget(null)}
                onNewMessage={() => { if (!isChatOpen) setChatUnread((n) => n + 1); }}
                onRequestOpen={openChat}
              />
            )}

            {/* Floating chat bubble (mobile bare-HUD; hidden on desktop via CSS) */}
            {currentRoom && showChat && !isChatOpen && (
              <button
                className="chat-bubble-fab"
                onClick={openChat}
                aria-label="Open chat"
              >
                💬
                {chatUnread > 0 && (
                  <span className="chat-bubble-badge">{chatUnread > 9 ? '9+' : chatUnread}</span>
                )}
              </button>
            )}

            {/* P3: room picker (invite this player to another room) */}
            {invitePickerTarget && (
              <RoomPickerSheet
                targetName={invitePickerTarget.name}
                currentRoomId={currentRoom?.id}
                onPick={(room: MyRoomSummary) => {
                  sendInviteToRoom(invitePickerTarget.id, room.id, room.name);
                  setInvitePickerTarget(null);
                }}
                onClose={() => setInvitePickerTarget(null)}
              />
            )}

            {/* P3: incoming invite banner (Accept / Decline) */}
            {incomingInvite && (
              <div className="room-invite-banner">
                <span className="room-invite-text">
                  🚪 <strong>{incomingInvite.fromName}</strong> invites you to <strong>{incomingInvite.roomName}</strong>
                </span>
                <div className="room-invite-actions">
                  <button className="room-invite-accept" onClick={() => respondInvite(true)}>Accept</button>
                  <button className="room-invite-decline" onClick={() => respondInvite(false)}>Decline</button>
                </div>
              </div>
            )}

            {/* P3: small transient note (invite sent / declined / full) */}
            {inviteNote && <div className="room-invite-note">{inviteNote}</div>}

            {/* P3: time-travel warp overlay (both parties) */}
            {travel && <TimeTravelOverlay roomName={travel.roomName} error={travel.error} />}

            {/* Sparks ✦ / satchel / gift flow (Alchemist's Cove P1) */}
            {currentRoom && (
              <SocialPlayPanel
                roomId={currentRoom.id}
                players={currentRoom.players}
              />
            )}

            {/* P5: co-op game HUD (top-center status strip + win/loss banner), rendered from
                the server-authoritative game_state. Layers on top of the social floor. */}
            {currentRoom && <GamePanel roomId={currentRoom.id} userId={user?.id} />}

            {/* DM / easter-egg world messages (centered toast over the game) */}
            <WorldMessageToast />
          </div>
        )}

        {currentView === 'connections' && (
          <SocialPanel />
        )}

        {currentView === 'preferences' && (
          <PreferencesPanel />
        )}
      </main>
    </div>
  );
}

function getStatusText(state: UnityGameState): string {
  switch (state) {
    case 'connecting':
      return 'Connecting...';
    case 'joining_room':
      return 'Joining...';
    case 'loading_scene':
      return 'Loading...';
    case 'spawning_player':
      return 'Preparing...';
    case 'disconnected':
      return 'Disconnected';
    case 'error':
      return 'Error';
    default:
      return '';
  }
}

/**
 * Wrapper for /room/:roomCode - shows RoomJoin join flow OR full game experience.
 * When the user is already in the room (or has just joined), RoomJoin passes
 * through to <AppContent /> which renders the game view with sidebar, chat, etc.
 */
function RoomJoinWrapper() {
  return (
    <RoomJoin>
      <AppContent />
    </RoomJoin>
  );
}

function App() {
  // Check URL for theme param (A/B testing: ?theme=royal-sapphire)
  const urlTheme = getThemeFromUrl();

  return (
    <BrowserRouter>
      <ThemeProvider initialTheme={urlTheme || undefined}>
        <SignalRProvider>
          <UserProvider>
            <CharacterProvider>
              <RoomProvider>
                <BillingProvider>
                  <ProfileCardProvider>
                    <SocialGraphProvider>
                      <PresenceProvider>
                        <ActivityProvider>
                          <Routes>
                            <Route path="/billing" element={<BillingPageWrapper />} />
                            <Route path="/billing/history" element={<BillingHistoryPageWrapper />} />
                            {/* /dungeon and /character-builder demo routes removed: the
                                DungeonGame/CharacterBuilder components are styled with
                                Tailwind classes but no Tailwind pipeline exists, so they
                                rendered unstyled. They are demo-only (no user-facing UI
                                links to them); the components stay in src/components/unity
                                for reference. */}
                            <Route path="/room/:roomCode" element={<RoomJoinWrapper />} />
                            <Route path="*" element={<AppContent />} />
                          </Routes>
                        </ActivityProvider>
                      </PresenceProvider>
                    </SocialGraphProvider>
                  </ProfileCardProvider>
                </BillingProvider>
              </RoomProvider>
            </CharacterProvider>
          </UserProvider>
        </SignalRProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

// Wrapper components for billing pages with layout
function BillingPageWrapper() {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();
  const [showProfileSetup, setShowProfileSetup] = useState(false);

  const handleViewChange = (view: View) => {
    if (view === 'profile') {
      setShowProfileSetup(true);
    } else if (view === 'billing') {
      navigate('/billing');
    } else {
      navigate('/');
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (showProfileSetup) {
    return <ProfilePage onClose={() => setShowProfileSetup(false)} />;
  }

  return (
    <div className="app-layout">
      <Sidebar currentView={'billing' as View} onViewChange={handleViewChange} />
      <main className="main-content">
        <BillingPage />
      </main>
    </div>
  );
}

function BillingHistoryPageWrapper() {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();
  const [showProfileSetup, setShowProfileSetup] = useState(false);

  const handleViewChange = (view: View) => {
    if (view === 'profile') {
      setShowProfileSetup(true);
    } else if (view === 'billing') {
      navigate('/billing');
    } else {
      navigate('/');
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (showProfileSetup) {
    return <ProfilePage onClose={() => setShowProfileSetup(false)} />;
  }

  return (
    <div className="app-layout">
      <Sidebar currentView={'billing' as View} onViewChange={handleViewChange} />
      <main className="main-content">
        <BillingHistoryPage />
      </main>
    </div>
  );
}

export default App;
